import {
  faEnvelope,
  faUser,
  faUserTag
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CreateEmployeeFormData } from 'data/from-data/create-employee-form.data';
import { Role } from 'data/users';
import { ChangeEvent, useEffect, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { GET_EMPLOYEE_ROLES } from 'services/api/employee';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface ProfileSetting {
  formData?: CreateEmployeeFormData;
  onDataChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  isAdmin?: boolean;
  onAdminStatusChange?: (isAdmin: boolean) => void;
  currentUserIsAdmin?: boolean;
  isFieldOperator?: boolean;
  onFieldOperatorStatusChange?: (isFieldOperator: boolean) => void;
  isCallOutUser?: boolean;
  onCallOutUserStatusChange?: (isCallOutUser: boolean) => void;
}

const SettingsPersonalInfo = ({
  formData,
  onDataChange,
  isAdmin = false,
  onAdminStatusChange,
  currentUserIsAdmin = false,
  isFieldOperator = false,
  onFieldOperatorStatusChange,
  isCallOutUser = false,
  onCallOutUserStatusChange
}: ProfileSetting) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const { data: rolesData, isLoading: isRolesLoading } = useQuery(
    'employeeRoles',
    GET_EMPLOYEE_ROLES,
    {
      staleTime: 3 * 60 * 60 * 1000
    }
  );

  useEffect(() => {
    if (rolesData) {
      setRoles(rolesData);
    }
  }, [rolesData]);

  const handlePhoneChange = (value: string) => {
    const formattedValue = value.startsWith('+') ? value : `+${value}`;

    const syntheticEvent = {
      target: {
        name: formData?.phoneNumber.name || '',
        value: formattedValue
      }
    };
    onDataChange && onDataChange(syntheticEvent as any);
  };

  return (
    <div className="mb-6">
      <h4 className="mb-4">Personal Information</h4>
      <Row className="g-3">
        <Col xs={12} sm={6}>
          <div className="form-icon-container">
            <Form.Floating>
              <Form.Control
                id="firstName"
                type="text"
                placeholder="First Name"
                className="form-icon-input"
                defaultValue={formData?.firstName.value}
                name={formData?.firstName.name}
                onChange={onDataChange}
                required={true}
              />
              <label htmlFor="firstName" className="form-icon-label text-700">
                FIRST NAME
              </label>
            </Form.Floating>
            <FontAwesomeIcon
              icon={faUser}
              className="text-900 fs-9 form-icon"
            />
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div className="form-icon-container">
            <Form.Floating>
              <Form.Control
                id="lastName"
                type="text"
                placeholder="Last Name"
                className="form-icon-input"
                defaultValue={formData?.lastName.value}
                name={formData?.lastName.name}
                onChange={onDataChange}
                required
              />
              <label htmlFor="lastName" className="form-icon-label text-700">
                LAST NAME
              </label>
            </Form.Floating>
            <FontAwesomeIcon
              icon={faUser}
              className="text-900 fs-9 form-icon"
            />
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div className="form-icon-container">
            <Form.Floating>
              <Form.Control
                id="email"
                type="email"
                placeholder="Enter your email"
                className="form-icon-input"
                defaultValue={formData?.email.value}
                name={formData?.email.name}
                onChange={onDataChange}
                required
              />
              <label htmlFor="email" className="form-icon-label text-700">
                ENTER YOUR EMAIL
              </label>
            </Form.Floating>
            <FontAwesomeIcon
              icon={faEnvelope}
              className="text-900 fs-9 form-icon"
            />
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div className="form-icon-container">
            <div className="position-relative">
              <PhoneInput
                country={'us'}
                value={formData?.phoneNumber.value || ''}
                onChange={handlePhoneChange}
                inputClass="form-icon-input"
                containerStyle={{ width: '100%' }}
                inputStyle={{
                  width: '100%',
                  height: '48px'
                }}
                specialLabel={''}
              />
            </div>
          </div>
        </Col>
        {/* Admin checkbox - only visible to admins */}
        {currentUserIsAdmin && (
          <div className="d-flex mb-3 gap-5">
            <div className="form-check">
              <Form.Check
                type="checkbox"
                id="isAdminCheck"
                label="Make this employee an admin"
                checked={isAdmin}
                onChange={e =>
                  onAdminStatusChange && onAdminStatusChange(e.target.checked)
                }
              />
            </div>
            <div className="form-check">
              <Form.Check
                type="checkbox"
                id="isFieldOperatorCheck"
                label="Make this employee a field operator"
                checked={isFieldOperator}
                onChange={e =>
                  onFieldOperatorStatusChange &&
                  onFieldOperatorStatusChange(e.target.checked)
                }
              />
            </div>
            <div className="form-check">
              <Form.Check
                type="checkbox"
                id="isCallOutUserCheck"
                label="Make this employee a Call-Out Cal user"
                checked={isCallOutUser}
                onChange={e =>
                  onCallOutUserStatusChange &&
                  onCallOutUserStatusChange(e.target.checked)
                }
              />
            </div>
          </div>
        )}
        {/* Roles dropdown - only visible when not an admin */}
        {!isAdmin && (
          <Col xs={12} sm={12}>
            {isRolesLoading ? (
              <Spinner animation="border" variant="primary" />
            ) : (
              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Select
                    id="roles"
                    name="roles"
                    className="form-icon-input"
                    onChange={onDataChange}
                    value={
                      (formData?.roles?.value?.[0] as any)?.id ||
                      formData?.roles?.value?.[0]?._id ||
                      ''
                    }
                    required={!isAdmin}
                  >
                    <option value="">Select Role</option>
                    {roles && roles?.length > 0 ? (
                      roles?.map(role => (
                        <option
                          key={(role as any).id || role._id}
                          value={(role as any).id || role._id}
                        >
                          {role.name}
                        </option>
                      ))
                    ) : (
                      <option value="">No roles available</option>
                    )}
                  </Form.Select>
                  <label htmlFor="roles" className="form-icon-label text-700">
                    SELECT ROLE
                  </label>
                </Form.Floating>
                <FontAwesomeIcon
                  icon={faUserTag}
                  className="text-900 fs-9 form-icon"
                />
              </div>
            )}
          </Col>
        )}
      </Row>
    </div>
  );
};

export default SettingsPersonalInfo;
